import React, { useState, useEffect, useCallback } from 'react';
import { getRoleName, useAuth } from '../../context/AuthContext';
import { TokenStorage } from '../../services/api';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUserPlus,
  faEnvelope,
  faXmark,
  faUserSlash,
  faUserCheck,
  faUserMinus,
  faCircleExclamation,
  faCheck,
  faUsers,
} from '@fortawesome/free-solid-svg-icons';
import '../../styles/TeamManagement.scss';

interface Member {
  user_id: number | string;
  email: string;
  full_name: string;
  role_name: string;
  is_active: boolean;
  is_verified: boolean;
  is_owner: boolean;
  created_at: string;
}

interface Invitation {
  invitation_id: string;
  email: string;
  role: string;
  status: string;
  invited_by_name: string;
  created_at: string;
  expires_at: string;
}

interface AccountData {
  account_id: number;
  account_name: string;
  account_type: string;
  owner_email: string;
  max_users: number;
  member_count: number;
  is_active: boolean;
  logo_url: string | null;
  website: string | null;
  created_at: string;
}

const BASE_URL = process.env.REACT_APP_API_URL || 'https://code.mannaplag.mannaanalytics.com';

async function apiFetch(endpoint: string, options?: RequestInit) {
  const token = TokenStorage.getAccess();
  const res = await fetch(`${BASE_URL}${endpoint}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options?.headers,
    },
  });
  return res.json();
}

const ADMIN_ONLY_TOOLTIP = 'Only admin has permission';

export default function TeamManagement() {
  const { user } = useAuth();
  const [account, setAccount] = useState<AccountData | null>(null);
  const [members, setMembers] = useState<Member[]>([]);
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [/* refreshing */, setRefreshing] = useState(false);
  const [showInvite, setShowInvite] = useState(false);
  const [inviteName, setInviteName] = useState('');
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<'admin' | 'member'>('member');
  const [inviting, setInviting] = useState(false);
  const [message, setMessage] = useState<{ text: string; type: 'success' | 'error' } | null>(null);
  const [confirmAction, setConfirmAction] = useState<{ member: Member; type: 'toggle' | 'remove' } | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [confirmRevoke, setConfirmRevoke] = useState<Invitation | null>(null);
  const [revoking, setRevoking] = useState(false);
  const [inviteActionLoading, setInviteActionLoading] = useState<{ id: string; type: 'resend' | 'revoke' } | null>(null);

  const showMsg = (text: string, type: 'success' | 'error' = 'success') => {
    setMessage({ text, type });
    setTimeout(() => setMessage(null), 4000);
  };

  const loadMembers = useCallback(async (opts?: { silent?: boolean }) => {
    if (opts?.silent) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    try {
      const data = await apiFetch('/accounts/members/');
      if (data.error) { showMsg(data.error, 'error'); return; }
      setAccount(data?.account);
      setMembers(data?.members);
      setInvitations(data?.invitations);
      setIsAdmin(data?.is_admin);
    } catch {
      showMsg('Failed to load team members.', 'error');
    } finally {
      if (opts?.silent) {
        setRefreshing(false);
      } else {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    loadMembers();
  }, [loadMembers]);

  const handleInvite = async () => {
    if (!inviteEmail.trim()) return;
    setInviting(true);
    try {
      const data = await apiFetch('/accounts/invite/', {
        method: 'POST',
        body: JSON.stringify({ email: inviteEmail, role: inviteRole, full_name: inviteName }),
      });
      if (data.error) { showMsg(data.error, 'error'); return; }
      showMsg(`Invitation sent to ${inviteEmail}`);
      closeInviteModal();
      await loadMembers({ silent: true })
    } catch {
      showMsg('Failed to send invitation.', 'error');
    } finally {
      setInviting(false);
    }
  };

  const handleRemove = async (userId: number | string) => {
    const data = await apiFetch(`/accounts/members/${userId}/`, { method: 'DELETE' });
    if (data.error) { showMsg(data.error, 'error'); return; }
    showMsg('Member removed.');
    await loadMembers({ silent: true })
  };

  const handleRoleChange = async (userId: number | string, newRole: string) => {
    const data = await apiFetch(`/accounts/members/${userId}/role/`, {
      method: 'PATCH',
      body: JSON.stringify({ role: newRole }),
    });
    if (data.error) { showMsg(data.error, 'error'); return; }
    showMsg('Role updated.');
    await loadMembers({ silent: true })
  };

  const handleToggleStatus = async (userId: number | string) => {
    const data = await apiFetch(`/accounts/members/${userId}/status/`, { method: 'PATCH' });
    if (data.error) { showMsg(data.error, 'error'); return; }
    showMsg(data.message);
    await loadMembers({ silent: true })
  };

  const handleConfirmAction = async () => {
    if (!confirmAction) return;
    setConfirming(true);
    try {
      if (confirmAction.type === 'remove') {
        await handleRemove(confirmAction.member.user_id);
      } else {
        await handleToggleStatus(confirmAction.member.user_id);
      }
      setConfirmAction(null);
    } finally {
      setConfirming(false);
    }
  };

  const handleRevokeInvite = async (inviteId: string, email: string) => {
    setInviteActionLoading({ id: inviteId, type: 'revoke' });
    try {
      const data = await apiFetch(`/accounts/invitations/${inviteId}/`, { method: 'DELETE' });
      if (data.error) { showMsg(data.error, 'error'); return; }
      showMsg('Invitation revoked.');
      await loadMembers({ silent: true }); // CHANGED
    } catch {
      showMsg('Failed to revoke invitation.', 'error');
    } finally {
      setInviteActionLoading(null);
    }
  };

  const handleConfirmRevoke = async () => {
    if (!confirmRevoke) return;
    setRevoking(true);
    try {
      await handleRevokeInvite(confirmRevoke.invitation_id, confirmRevoke.email);
      setConfirmRevoke(null);
    } finally {
      setRevoking(false);
    }
  };
  const handleResendInvite = async (inviteId: string, email: string) => {
    setInviteActionLoading({ id: inviteId, type: 'resend' });
    try {
      const data = await apiFetch(`/accounts/invitations/${inviteId}/resend/`, { method: 'POST' });
      if (data.error) { showMsg(data.error, 'error'); return; }
      showMsg(`Invitation resent to ${email}`);
      await loadMembers({ silent: true }); // CHANGED
    } catch {
      showMsg('Failed to resend invitation.', 'error');
    } finally {
      setInviteActionLoading(null);
    }
  };

  // Researcher guard
  if (getRoleName(user?.role).toLowerCase() === 'researcher') {
    return (
      <div className="team-page">
        <div className="team-header">
          <p className="team-header__title">Team management</p>
        </div>
        <div className="team-guard">
          <div className="team-guard__card">
            <FontAwesomeIcon icon={faUsers} className="team-guard__icon" />
            <p className="team-guard__title">Not available on your plan</p>
            <p className="team-guard__desc">Team management is available on Institutional and Publisher plans.</p>
          </div>
        </div>
      </div>
    );
  }
  const closeInviteModal = () => {
    setShowInvite(false);
    setInviteName('');
    setInviteEmail('');
    setInviteRole('member');
  };

  return (
    <div className="team-page">

      {/* Page header */}
      <div className="team-header">
        <div>
          <p className="team-header__title">Team management</p>
          <p className="team-header__subtitle">
            {account ? `${account.account_name || 'Your account'} · ${members.length} of ${account.max_users} seats used` : 'Manage your team'}
          </p>
        </div>
        <button onClick={() => setShowInvite(true)} className="btn-invite">
          <FontAwesomeIcon icon={faUserPlus} style={{ fontSize: 14 }} />
          Invite member
        </button>
      </div>

      {/* Toast message — fixed, above modal overlay */}
      {message && (
        <div className={`toast alert alert--${message.type}`}>
          <FontAwesomeIcon icon={message.type === 'success' ? faCheck : faCircleExclamation} style={{ fontSize: 14 }} />
          <p>{message.text}</p>
        </div>
      )}

      <div className="team-content">

        {/* ── Invite modal ── */}
        {showInvite && (
          <div className="modal-overlay">
            <div className="modal">

              {/* Modal header */}
              <div className="modal__header">
                <div>
                  <p className="modal__title">Invite member</p>
                  <p className="modal__subtitle">Send an email invitation to join your team</p>
                </div>
                <button onClick={closeInviteModal} className="modal__close">
                  <FontAwesomeIcon icon={faXmark} />
                </button>
              </div>

              {/* Modal body */}
              <div className="modal__body">

                {/* Name */}
                <div className="form-group">
                  <label className="form-group__label">
                    Full name <span className="optional">(optional)</span>
                  </label>
                  <input
                    type="text"
                    value={inviteName}
                    onChange={e => setInviteName(e.target.value)}
                    placeholder="e.g. Dr. Priya Sharma"
                    className="form-group__input"
                  />
                </div>

                {/* Email */}
                <div className="form-group">
                  <label className="form-group__label">
                    Email address <span className="required">*</span>
                  </label>
                  <input
                    type="email"
                    value={inviteEmail}
                    onChange={e => setInviteEmail(e.target.value)}
                    placeholder="colleague@institution.edu"
                    onKeyDown={e => e.key === 'Enter' && handleInvite()}
                    className="form-group__input"
                  />
                </div>

                {/* Role — checkboxes (mutually exclusive: User / Admin) */}
                <div className="form-group">
                  <label className="form-group__label">
                    Role <span className="required">*</span>
                  </label>
                  <div className="role-checkboxes">
                    {(['member', 'admin'] as const).map(r => (
                      <label key={r} className="role-checkbox" onClick={() => setInviteRole(r)}>
                        <input
                          type="checkbox"
                          checked={inviteRole === r}
                          onChange={() => setInviteRole(r)}
                        />
                        <span className={`role-checkbox__label ${inviteRole === r ? 'role-checkbox__label--active' : ''}`}>
                          {r === 'member' ? 'User' : 'Admin'}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>

              </div>

              {/* Modal footer */}
              <div className="modal__footer">
                <button onClick={closeInviteModal} className="btn-cancel">
                  Cancel
                </button>
                <button onClick={handleInvite} disabled={inviting || !inviteEmail.trim()} className="btn-submit">
                  {inviting ? 'Sending...' : (<><FontAwesomeIcon icon={faEnvelope} style={{ fontSize: 12 }} /> Send invitation</>)}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── Confirm action modal (disable / remove member) ── */}
        {confirmAction && (
          <div className="modal-overlay">
            <div className="modal modal--details">

              <div className="modal--details__body">
                <p className="modal--details__title">User details</p>

                <div className="user-summary">
                  <div className="member-avatar">
                    {(confirmAction.member.full_name || confirmAction.member.email).slice(0, 2).toUpperCase()}
                  </div>
                  <div>
                    <p className="user-summary__name">{confirmAction.member.full_name || confirmAction.member.email}</p>
                    <p className="user-summary__email">{confirmAction.member.email}</p>
                  </div>
                </div>

                <div className="detail-grid">
                  <div>
                    <p className="detail-grid__label">Role</p>
                    <p className="detail-grid__value">
                      {confirmAction.member.role_name
                        ? confirmAction.member.role_name.charAt(0).toUpperCase() + confirmAction.member.role_name.slice(1)
                        : '—'}
                    </p>
                  </div>
                  <div>
                    <p className="detail-grid__label">Organisation</p>
                    <p className="detail-grid__value">{account?.account_name || '—'}</p>
                  </div>
                  <div>
                    <p className="detail-grid__label">Status</p>
                    <span className={`badge ${confirmAction.member.is_active ? 'badge--status-active' : 'badge--status-disabled'}`}>
                      {confirmAction.member.is_active ? 'Active' : 'Disabled'}
                    </span>
                  </div>
                  <div>
                    <p className="detail-grid__label">Verified</p>
                    <span className={`badge ${confirmAction.member.is_verified ? 'badge--status-active' : 'badge--status-disabled'}`}>
                      {confirmAction.member.is_verified ? 'Yes' : 'No'}
                    </span>
                  </div>
                  <div>
                    <p className="detail-grid__label">Joined</p>
                    <p className="detail-grid__value">
                      {confirmAction.member.created_at
                        ? new Date(confirmAction.member.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
                        : '—'}
                    </p>
                  </div>
                </div>
              </div>

              <div className="modal__footer">
                <button onClick={() => setConfirmAction(null)} className="btn-cancel">
                  Close
                </button>
                <button onClick={handleConfirmAction} disabled={confirming} className="btn-danger">
                  <FontAwesomeIcon icon={confirmAction.type === 'remove' ? faUserMinus : faUserSlash} style={{ fontSize: 12 }} />
                  {confirming
                    ? 'Please wait...'
                    : confirmAction.type === 'remove'
                      ? 'Remove user'
                      : confirmAction.member.is_active ? 'Disable user' : 'Enable user'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── Confirm revoke invitation modal — same style as suspend/remove ── */}
        {confirmRevoke && (
          <div className="modal-overlay">
            <div className="modal modal--details">

              <div className="modal--details__body">
                <p className="modal--details__title">Revoke invitation</p>

                <div className="user-summary">
                  <div className="member-avatar member-avatar--pending">
                    <FontAwesomeIcon icon={faEnvelope} style={{ fontSize: 13 }} />
                  </div>
                  <div>
                    <p className="user-summary__name">{confirmRevoke.email}</p>
                    <p className="user-summary__email">
                      Invited as {confirmRevoke.role.charAt(0).toUpperCase() + confirmRevoke.role.slice(1)}
                    </p>
                  </div>
                </div>

                <p className="revoke-warning">
                  This invitation link will stop working immediately. {confirmRevoke.email} won't be able to join using it.
                </p>
              </div>

              <div className="modal__footer">
                <button onClick={() => setConfirmRevoke(null)} className="btn-cancel">
                  Close
                </button>
                <button
                  onClick={handleConfirmRevoke}
                  disabled={revoking}
                  className="btn-danger"
                >
                  <FontAwesomeIcon
                    icon={faXmark}
                    style={{ fontSize: 12, color: "#fff" }}
                  />
                  {revoking ? "Please wait..." : "Revoke invitation"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── Silent refresh indicator — navy pill, mirrors btn-invite style, page stays visible ── */}
        {inviteActionLoading && (
          <div className="loading-pill">
            <span className="loading-pill__spinner" />
            Updating...
          </div>
        )}
        {loading ? (
          <div className="spinner-wrap">
            <div className="spinner" />
          </div>
        ) : (
          <>
            {/* Unified members + pending invitations table */}
            <div className="card">
              <div className="card__header">
                <p className="card__title">
                  Members <span className="count">({members.length + invitations.length})</span>
                </p>
                {account && (
                  <p className="card__meta">
                    {account.max_users - members.length - invitations.length} seats remaining
                  </p>
                )}
              </div>

              {/* Column headers */}
              <div className="table-header table-header--members">
                <span>Member</span>
                <span>Email</span>
                <span>Role</span>
                <span className="align-center">Status</span>
                <span className="align-center">Actions</span>
              </div>

              {members.length === 0 && invitations.length === 0 ? (
                <div className="empty-state">
                  <p>No members yet. Invite someone to get started.</p>
                </div>
              ) : (
                <>
                  {/* Active / disabled members */}
                  {members.map(m => {
                    const isSelf =
                      String(m.user_id) === String(user?.id) ||
                      (!!user?.email && m.email?.toLowerCase() === user.email.toLowerCase());
                    const isOwner = m.is_owner;
                    const actionsDisabled = !isAdmin || isSelf || isOwner;

                    return (
                      <div key={m.user_id} className={`member-row ${!m.is_active ? 'member-row--inactive' : ''}`}>
                        {/* Identity */}
                        <div className="member-identity">
                          <div className="member-avatar">
                            {(m.full_name || m.email).slice(0, 2).toUpperCase()}
                          </div>
                          <div className="member-meta">
                            <div className="member-meta__row">
                              <p className="member-meta__name">{m.full_name || m.email}</p>
                              {isSelf && <span className="tag-you">You</span>}
                              {isOwner && <span className="tag-owner">Owner</span>}
                            </div>
                            {!m.is_verified && (
                              <span className="member-meta__unverified">Unverified</span>
                            )}
                          </div>
                        </div>

                        {/* Email */}
                        <p className="member-email">{m.email}</p>

                        {/* Role */}
                        {isAdmin && !isSelf && !isOwner ? (
                          <select
                            value={m.role_name}
                            onChange={e => handleRoleChange(m.user_id, e.target.value)}
                            className="role-select"
                          >
                            <option value="member">Member</option>
                            <option value="admin">Admin</option>
                          </select>
                        ) : (
                          <span className={`badge ${m.role_name === 'admin' || isOwner ? 'badge--role-admin' : 'badge--role-member'}`}>
                            {m.role_name ? m.role_name.charAt(0).toUpperCase() + m.role_name.slice(1) : '—'}
                          </span>
                        )}

                        {/* Status */}
                        <span className={`badge ${m.is_active ? 'badge--status-active' : 'badge--status-disabled'}`}>
                          {m.is_active ? 'Active' : 'Disabled'}
                        </span>

                        {/* Actions */}
                        <div className="actions">
                          <span className="tooltip" data-tooltip={actionsDisabled ? (isOwner ? 'Owner cannot be modified' : ADMIN_ONLY_TOOLTIP) : undefined}>
                            <button
                              onClick={() => !actionsDisabled && setConfirmAction({ member: m, type: 'toggle' })}
                              disabled={actionsDisabled}
                              title={actionsDisabled ? (isOwner ? 'Owner cannot be modified' : ADMIN_ONLY_TOOLTIP) : (m.is_active ? 'Disable member' : 'Enable member')}
                              className={`icon-btn ${m.is_active ? 'icon-btn--warning' : 'icon-btn--success'}`}
                            >
                              <FontAwesomeIcon icon={m.is_active ? faUserSlash : faUserCheck} style={{ fontSize: 13 }} />
                            </button>
                          </span>
                          <span className="tooltip" data-tooltip={actionsDisabled ? (isOwner ? 'Owner cannot be removed' : ADMIN_ONLY_TOOLTIP) : undefined}>
                            <button
                              onClick={() => !actionsDisabled && setConfirmAction({ member: m, type: 'remove' })}
                              disabled={actionsDisabled}
                              title={actionsDisabled ? (isOwner ? 'Owner cannot be removed' : ADMIN_ONLY_TOOLTIP) : 'Remove member'}
                              className="icon-btn icon-btn--neutral"
                            >
                              <FontAwesomeIcon icon={faUserMinus} style={{ fontSize: 13 }} />
                            </button>
                          </span>
                        </div>
                      </div>
                    );
                  })}

                  {/* Pending invitations — in same table */}
                  {invitations.map(inv => (
                    <div key={inv.invitation_id} className="member-row member-row--pending">
                      {/* Identity */}
                      <div className="member-identity">
                        <div className="member-avatar member-avatar--pending">
                          <FontAwesomeIcon icon={faEnvelope} style={{ fontSize: 13 }} />
                        </div>
                        <div className="member-meta">
                          <p className="member-meta__name">{inv.email}</p>
                          <p className="member-meta__unverified">
                            Expires {new Date(inv.expires_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                          </p>
                        </div>
                      </div>

                      {/* Email */}
                      <p className="member-email">{inv.email}</p>

                      {/* Role */}
                      <span className={`badge ${inv.role === 'admin' ? 'badge--role-admin' : 'badge--role-member'}`}>
                        {inv.role.charAt(0).toUpperCase() + inv.role.slice(1)}
                      </span>

                      {/* Status */}
                      <span className="badge badge--status-pending">Pending</span>

                      {/* Actions */}
                      <div className="actions">
                        <span className="tooltip" data-tooltip={!isAdmin ? ADMIN_ONLY_TOOLTIP : 'Resend invitation'}>
                          <button
                            onClick={() => isAdmin && handleResendInvite(inv.invitation_id, inv.email)}
                            disabled={!isAdmin || !!inviteActionLoading}
                            title={!isAdmin ? ADMIN_ONLY_TOOLTIP : 'Resend invitation'}
                            className="icon-btn icon-btn--success"
                          >
                            {inviteActionLoading?.id === inv.invitation_id && inviteActionLoading.type === 'resend' ? (
                              <span className="icon-btn__spinner" />
                            ) : (
                              <FontAwesomeIcon icon={faEnvelope} style={{ fontSize: 13 }} />
                            )}
                          </button>
                        </span>
                        <span className="tooltip" data-tooltip={!isAdmin ? ADMIN_ONLY_TOOLTIP : 'Revoke invitation'}>
                          <button
                            onClick={() => isAdmin && setConfirmRevoke(inv)}
                            disabled={!isAdmin || !!inviteActionLoading}
                            title={!isAdmin ? ADMIN_ONLY_TOOLTIP : 'Revoke invitation'}
                            className="icon-btn icon-btn--neutral"
                          >
                            {inviteActionLoading?.id === inv.invitation_id && inviteActionLoading.type === 'revoke' ? (
                              <span className="icon-btn__spinner" />
                            ) : (
                              <FontAwesomeIcon icon={faXmark} style={{ fontSize: 13 }} />
                            )}
                          </button>
                        </span>
                      </div>
                    </div>
                  ))}
                </>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}